/* eslint-disable sonarjs/cognitive-complexity -- the drawer intentionally coordinates explicit loading, error, empty, and evidence states */
import type { ObservabilityUsageSubjectResponse } from '@scatterin/api-types';
import { ArrowUpRight, Database, DollarSign, FolderKanban, Gauge, HardDrive } from 'lucide-react';
import { Link } from 'react-router';

import { EmptyState, ErrorState, LoadingState, StateView } from '@/components/stateViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Drawer } from '@/components/ui/drawer';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import type { useUsageSubjectQuery } from '@/hooks/useObservabilityQueries';
import { formatBytes, formatDuration, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { drillThroughSearch } from '@/lib/navigation';

type SubjectQuery = ReturnType<typeof useUsageSubjectQuery>;

const formatUsd = (value: number | undefined): string =>
  (value ?? 0).toLocaleString(undefined, {
    currency: 'USD',
    maximumFractionDigits: value && value < 0.01 ? 6 : 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });

const label = (value: string): string =>
  value
    .replaceAll('_', ' ')
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll(/^./g, (character) => character.toUpperCase());

const subjectSearch = (currentSearch: string, subjectType: 'project' | 'user', subjectId: string): string => {
  const search = new URLSearchParams(currentSearch);
  search.delete(subjectType === 'project' ? 'userId' : 'projectId');
  search.set(subjectType === 'project' ? 'projectId' : 'userId', subjectId);
  search.set('view', subjectType === 'project' ? 'projects' : 'users');
  return search.toString();
};

const SummaryCards = ({ data }: { data: ObservabilityUsageSubjectResponse }) => {
  const timedRuns = data.workflows?.reduce((total, workflow) => total + (workflow.timedRuns ?? 0), 0) ?? 0;
  const values = [
    { icon: <HardDrive className="size-4" />, label: 'Durable storage', value: formatBytes(data.storage.bytes) },
    { icon: <Database className="size-4" />, label: 'Stored objects', value: formatInteger(data.storage.objects) },
    { icon: <Gauge className="size-4" />, label: 'Timed runs', value: formatInteger(timedRuns) },
    {
      icon: <DollarSign className="size-4" />,
      label: 'Period compute estimate',
      value: data.cost.isEstimateAvailable ? formatUsd(data.cost.periodEstimatedUsd) : 'Unavailable',
    },
  ];
  return (
    <dl className="grid grid-cols-2 gap-3">
      {values.map((item) => (
        <div className="border-line rounded-lg border p-3" key={item.label}>
          <dt className="text-muted flex items-center gap-2 text-xs font-semibold">
            {item.icon}
            {item.label}
          </dt>
          <dd className="text-ink numeric mt-2 text-lg font-semibold">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export const UsageSubjectDrawer = ({
  close,
  currentSearch,
  identityLabel,
  query,
  subjectId,
  subjectType,
}: {
  close: () => void;
  currentSearch: string;
  identityLabel?: string;
  query: SubjectQuery;
  subjectId?: string;
  subjectType?: 'project' | 'user';
}) => {
  const data = query.data;
  const projectName = data?.subjectType === 'project' ? data.projects?.[0]?.projectName : undefined;
  return (
    <Drawer
      description={subjectId ? `${label(subjectType ?? 'user')} ID ${subjectId}` : 'The selected usage subject is loading.'}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      open={Boolean(subjectId && subjectType)}
      side="right"
      size="lg"
      title={projectName ?? identityLabel ?? (subjectType === 'project' ? 'Project 360' : 'User 360')}
    >
      {query.isLoading ? <LoadingState label={`Loading ${subjectType ?? 'subject'} 360`} /> : null}
      {query.isError ? <ErrorState message={query.error.message} onRetry={() => void query.refetch()} /> : null}
      {data ? (
        <div className="grid gap-6">
          <SummaryCards data={data} />

          <StateView
            title={
              data.storage.hasReconciledInventory
                ? 'All storage categories have a completed baseline'
                : `${data.storage.reconciledCategories} of 3 storage categories have a completed baseline`
            }
            tone={data.storage.hasReconciledInventory ? 'healthy' : 'coverage'}
          >
            Current total {formatBytes(data.storage.bytes)} across {formatInteger(data.storage.objects)} live objects. The selected period
            added {formatBytes(data.storage.createdBytesInWindow)} and removed {formatBytes(data.storage.deletedBytesInWindow)}.{' '}
            {formatInteger(data.storage.reconciledObjects)} live objects came from bounded reconciliation.
          </StateView>

          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Storage composition</h3>
            <div className="grid gap-3 sm:grid-cols-3">
              {(data.storage.byCategory ?? []).map((category) => (
                <Card key={category.category}>
                  <CardContent className="grid gap-1">
                    <p className="text-muted text-xs font-semibold">{label(category.category)}</p>
                    <p className="text-ink numeric text-lg font-semibold">{formatBytes(category.bytes)}</p>
                    <p className="text-muted numeric text-xs">{formatInteger(category.objects)} objects</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {data.subjectType === 'user' ? (
            <section className="grid gap-3">
              <h3 className="text-ink text-sm font-semibold">Projects</h3>
              {(data.projects ?? []).length === 0 ? (
                <EmptyState title="No projects found" tone="coverage" />
              ) : (
                <div className="grid gap-2">
                  {(data.projects ?? []).map((project) => (
                    <Button asChild key={project.projectId} variant="secondary">
                      <Link to={{ pathname: '/usage', search: subjectSearch(currentSearch, 'project', project.projectId) }}>
                        <FolderKanban className="size-4" />
                        <span className="min-w-0 flex-1 truncate text-left">{project.projectName ?? project.projectId}</span>
                        <span className="numeric text-muted">{formatInteger(project.runs)} runs</span>
                      </Link>
                    </Button>
                  ))}
                </div>
              )}
            </section>
          ) : null}

          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Workflow coverage scorecard</h3>
            <TableShell>
              <Table aria-label="Workflow coverage" style={{ minWidth: '42rem' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Runs</TableHead>
                    <TableHead>Projects</TableHead>
                    <TableHead>Timed</TableHead>
                    <TableHead>P50 / P95</TableHead>
                    <TableHead>Last run</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.workflows ?? []).map((workflow) => (
                    <TableRow key={workflow.analysisType}>
                      <TableCell>
                        <span className="text-ink font-semibold">{label(workflow.analysisType)}</span>
                        <Badge className="ml-2" tone={(workflow.runs ?? 0) > 0 ? 'green' : 'slate'}>
                          {(workflow.runs ?? 0) > 0 ? 'covered' : 'no evidence'}
                        </Badge>
                      </TableCell>
                      <TableCell className="numeric">{formatInteger(workflow.runs)}</TableCell>
                      <TableCell className="numeric">{formatInteger(workflow.projects)}</TableCell>
                      <TableCell className="numeric">{formatInteger(workflow.timedRuns)}</TableCell>
                      <TableCell className="numeric">
                        {formatDuration(workflow.duration.p50, workflow.duration.unit)} /{' '}
                        {formatDuration(workflow.duration.p95, workflow.duration.unit)}
                      </TableCell>
                      <TableCell title={formatTime(workflow.lastRunAt)}>{formatRelativeTime(workflow.lastRunAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          </section>

          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Daily performance history</h3>
            {(data.performanceHistory ?? []).length === 0 ? (
              <EmptyState title="No timed history in this period" tone="coverage" />
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {(data.performanceHistory ?? []).map((period) => (
                  <div className="border-line rounded-lg border p-3" key={period.periodStart}>
                    <p className="text-ink text-sm font-semibold">{new Date(period.periodStart).toLocaleDateString()}</p>
                    <p className="text-muted numeric mt-1 text-xs">
                      {formatInteger(period.runs)} runs · {formatInteger(period.timedRuns)} timed
                    </p>
                    <p className="text-ink numeric mt-2 text-sm">
                      P50 {formatDuration(period.duration.p50, period.duration.unit)} · P95{' '}
                      {formatDuration(period.duration.p95, period.duration.unit)} · P99{' '}
                      {formatDuration(period.duration.p99, period.duration.unit)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Attributed cost estimate</h3>
            <div className="grid gap-2">
              {(data.cost.components ?? []).map((component) => (
                <div className="border-line grid gap-1 rounded-lg border p-3" key={component.key}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-ink text-sm font-semibold">{component.label}</p>
                    <p className="text-ink numeric font-semibold">
                      {component.isAvailable ? formatUsd(component.estimatedUsd) : 'Unavailable'}
                    </p>
                  </div>
                  <p className="text-muted text-xs">{component.coverage}</p>
                  <p className="text-muted text-xs">{component.basis}</p>
                </div>
              ))}
            </div>
            <p className="text-muted text-xs">
              {data.cost.methodology} Rate card: {data.cost.rateCardVersion}.
            </p>
          </section>

          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Analysis history</h3>
            {(data.recentRuns ?? []).length === 0 ? (
              <EmptyState title="No runs in this period" tone="coverage" />
            ) : (
              <div className="grid gap-2">
                {(data.recentRuns ?? []).map((run) => (
                  <div className="border-line flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3" key={run.runId}>
                    <div className="min-w-0">
                      <p className="text-ink truncate text-sm font-semibold">
                        {label(run.analysisType ?? run.jobKind ?? 'Unknown workflow')}
                      </p>
                      <p className="text-muted mt-1 text-xs">
                        {formatTime(run.observedAt)} · {formatDuration(run.endToEndDurationMs, 'ms')}
                      </p>
                    </div>
                    <Button asChild variant="ghost">
                      <Link
                        to={{ pathname: '/performance', search: drillThroughSearch(currentSearch, { runId: run.runId, view: 'runs' }) }}
                      >
                        Details <ArrowUpRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      ) : null}
    </Drawer>
  );
};
